import { Button, ChevronRight } from '@chain-zen/ui'
import { useState } from 'react'

import type { TDispatcher } from '../../../../../types/shipment'
import { TruckTrailerSelectionPopup } from './truck-trailer-selection-popup'

interface IShipmentTrailerTruckProps {
  dispatchers?: TDispatcher[]
  shipmentID: string
  shipmentOwnerWorkspaceID: string
  trailerNumber?: string | null
  truckNumber?: string | null
}

/**
 * Displays a single combined box for trailer and truck numbers.
 * Clicking opens a unified modal with tabs to switch between trailer/truck selection.
 * The modal uses ConfirmationModal from @chain-zen/ui for removal and replacement confirmations.
 */
export const ShipmentTrailerTruck = ({
  dispatchers,
  shipmentID,
  shipmentOwnerWorkspaceID,
  trailerNumber: initialTrailerNumber,
  truckNumber: initialTruckNumber
}: IShipmentTrailerTruckProps) => {
  const [modalOpen, setModalOpen] = useState(false)
  const [trailerNumber, setTrailerNumber] = useState<string | null>(initialTrailerNumber ?? null)
  const [truckNumber, setTruckNumber] = useState<string | null>(initialTruckNumber ?? null)

  const hasAnyValue = !!trailerNumber || !!truckNumber

  return (
    <>
      <Button
        onClick={() => setModalOpen(true)}
        variant="outline"
        className="h-auto w-full justify-start overflow-visible rounded-md border-border bg-background p-3 text-left shadow-none transition-colors hover:bg-muted/40 hover:shadow-none group"
      >
        <div className="flex flex-col w-full overflow-hidden">
          <div className="flex items-start md:items-center justify-between w-full gap-2">
            <div className="flex flex-col md:flex-row md:items-center gap-1.5 md:gap-4 flex-1 min-w-0">
              {/* Trailer section */}
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <span className="text-muted-foreground text-sm shrink-0">Trailer:</span>
                <div className="relative min-w-0 group/trailer flex-1">
                  <span
                    className={`text-sm truncate block ${trailerNumber ? 'text-foreground font-medium' : 'text-muted-foreground'}`}
                  >
                    {trailerNumber || 'Not set'}
                  </span>
                  {!!trailerNumber && (
                    <div className="absolute bottom-full left-0 mb-1 px-2 py-1 bg-popover border border-border rounded text-xs text-popover-foreground whitespace-nowrap opacity-0 invisible group-hover/trailer:opacity-100 group-hover/trailer:visible transition-opacity z-50 pointer-events-none">
                      {trailerNumber}
                    </div>
                  )}
                </div>
              </div>

              <div className="hidden md:block text-muted-foreground shrink-0">|</div>

              {/* Truck section */}
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <span className="text-muted-foreground text-sm shrink-0">Truck:</span>
                <div className="relative min-w-0 group/truck flex-1">
                  <span
                    className={`text-sm truncate block ${truckNumber ? 'text-foreground font-medium' : 'text-muted-foreground'}`}
                  >
                    {truckNumber || 'Not set'}
                  </span>
                  {!!truckNumber && (
                    <div className="absolute bottom-full left-0 mb-1 px-2 py-1 bg-popover border border-border rounded text-xs text-popover-foreground whitespace-nowrap opacity-0 invisible group-hover/truck:opacity-100 group-hover/truck:visible transition-opacity z-50 pointer-events-none">
                      {truckNumber}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Chevron */}
            <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-1 group-hover:text-foreground" />
          </div>

          {/* Helper text when nothing is set */}
          {!hasAnyValue && (
            <div className="mt-2 text-xs text-muted-foreground whitespace-normal break-words">
              Click to select trailer or truck from your ELD apps
            </div>
          )}
        </div>
      </Button>

      {/* Unified Selection Modal */}
      <TruckTrailerSelectionPopup
        currentTrailerNumber={trailerNumber}
        currentTruckNumber={truckNumber}
        dispatchers={dispatchers}
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onTrailerChange={setTrailerNumber}
        onTruckChange={setTruckNumber}
        shipmentID={shipmentID}
        shipmentOwnerWorkspaceID={shipmentOwnerWorkspaceID}
      />
    </>
  )
}
